import { useState } from "react";

import { listClients, listUsers, updateUser } from "../utils/adminFunctions";
import { addNotification } from "../Notification";
import { t } from "../../i18n/language";
import Spinner from "../Spinner";
import Button from "../Button";
import Input from "../Input";

/**
 * Collapse the tag lists of every user in an org into one list.
 *
 * Tags are stored per-user (`users/{uid}.default_filter_tags`) but managed per-org
 * here, so an org whose users were seeded by hand can hold differing lists. The
 * union is the safe starting point — it shows every tag anyone has rather than
 * silently dropping some the moment an admin hits save.
 */
const mergeTags = (users) => {
  const byName = new Map();

  users.forEach((user) => {
    const tags = Array.isArray(user.default_filter_tags)
      ? user.default_filter_tags
      : [];

    tags.forEach((tag) => {
      if (!tag || typeof tag.name !== "string") return;
      const key = tag.name.trim().toLowerCase();
      if (!key || byName.has(key)) return;
      byName.set(key, {
        name: tag.name.trim(),
        default_checked: tag.default_checked === true,
      });
    });
  });

  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
};

/** True when the org's users don't all hold the same tag list. */
const usersDiverge = (users, merged) => {
  const fingerprint = (tags) =>
    [...tags]
      .map((tag) => `${tag.name.trim().toLowerCase()}:${tag.default_checked === true}`)
      .sort()
      .join("|");

  const target = fingerprint(merged);

  return users.some((user) => {
    const tags = Array.isArray(user.default_filter_tags)
      ? user.default_filter_tags
      : [];
    return fingerprint(tags) !== target;
  });
};

function Tags() {
  const [clients, setClients] = useState();
  const [allUsers, setAllUsers] = useState();
  const [orgId, setOrgId] = useState("");
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  if (!clients && !allUsers && !loadError) {
    Promise.all([listClients(), listUsers()])
      .then(([orgs, users]) => {
        orgs.sort((a, b) => a.name.localeCompare(b.name));
        setClients(orgs);
        setAllUsers(users);
      })
      .catch((err) => {
        console.error("Failed to load orgs/users for tag admin", err);
        setLoadError(true);
      });
  }

  const orgUsers = allUsers
    ? allUsers.filter((user) => user.org_id === orgId)
    : [];

  const handleOrgChange = (nextOrgId) => {
    setOrgId(nextOrgId);
    setNewTag("");
    const users = allUsers.filter((user) => user.org_id === nextOrgId);
    setTags(mergeTags(users));
  };

  const handleAddTag = () => {
    const name = newTag.trim();
    if (!name) return;

    const exists = tags.some(
      (tag) => tag.name.toLowerCase() === name.toLowerCase()
    );
    if (exists) {
      addNotification(
        t("Tag already added"),
        `"${name}"`,
        "bg-red-600"
      );
      return;
    }

    setTags(
      [...tags, { name, default_checked: false }].sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    );
    setNewTag("");
  };

  const handleToggle = (name) => {
    setTags(
      tags.map((tag) =>
        tag.name === name
          ? { ...tag, default_checked: !tag.default_checked }
          : tag
      )
    );
  };

  const handleDelete = (name) => {
    setTags(tags.filter((tag) => tag.name !== name));
  };

  const handleSave = () => {
    if (orgUsers.length === 0) return;
    setSaving(true);

    Promise.all(
      orgUsers.map((user) =>
        updateUser({ uid: user.uid, default_filter_tags: tags })
      )
    )
      .then(() => {
        // Keep the in-memory user list in step so switching orgs and back
        // doesn't show the pre-save tags.
        setAllUsers(
          allUsers.map((user) =>
            user.org_id === orgId ? { ...user, default_filter_tags: tags } : user
          )
        );
        setSaving(false);
        addNotification(
          t("Tags saved"),
          `${orgUsers.length} ${t("users updated")}`,
          undefined
        );
      })
      .catch((err) => {
        console.error("Failed to save tags", err);
        setSaving(false);
        addNotification(
          t("Error"),
          t("We couldn't save the tags right now. Please try again."),
          "bg-red-600"
        );
      });
  };

  const diverged = orgId && orgUsers.length > 0 && usersDiverge(orgUsers, tags);

  return (
    <>
      <h1 className="text-2xl font-semibold pb-3">{t("Tags")}</h1>
      <p className="text-sm text-gray-600 pb-4">
        {t(
          "Tags filter the video list in the sidebar. They must match the tags used on the videos in Brightcove."
        )}
      </p>

      {loadError && (
        <p className="text-sm text-red-600">
          {t("We couldn't load the accounts. Please reload the page.")}
        </p>
      )}
      {!clients && !loadError && <Spinner />}

      {clients && (
        <>
          <div className="flex flex-col">
            <label className="text-sm" htmlFor="org">
              {t("Account")}
            </label>
            <select
              id="org"
              name="org"
              className="mt-2 px-2 py-2 border border-gray-200 rounded focus:outline-none focus:shadow-outline"
              value={orgId}
              onChange={(e) => handleOrgChange(e.target.value)}
            >
              <option value="">{t("Choose an account...")}</option>
              {clients.map((client) => (
                <option key={client.org_id} value={client.org_id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>

          {orgId && (
            <>
              <p className="mt-2 text-sm text-gray-600">
                {orgUsers.length} {t("users in this account")}
              </p>

              {orgUsers.length === 0 && (
                <p className="mt-4 text-sm font-semibold">
                  {t(
                    "This account has no users yet, so there is nowhere to save tags."
                  )}
                </p>
              )}

              {diverged && (
                <p className="mt-4 text-sm text-gray-700 bg-yellow-100 border border-yellow-300 rounded px-3 py-2">
                  {t(
                    "Users in this account currently have different tag lists. The list below merges them all; saving gives every user this same list."
                  )}
                </p>
              )}

              <div className="mt-4 bg-white rounded border border-gray-200 text-sm">
                <div className="px-3 py-2 grid grid-cols-10 text-xs text-gray-600 uppercase font-semibold leading-relaxed">
                  <div className="col-span-7">{t("Tag")}</div>
                  <div className="col-span-2 mx-auto">{t("Default")}</div>
                  <div className="col-span-1 mx-auto">{t("Delete")}</div>
                </div>

                {tags.length === 0 && (
                  <div className="px-3 py-4 grid grid-cols-10 border-t border-gray-200">
                    <div className="col-span-full font-semibold">
                      {t("No tags added")}
                    </div>
                  </div>
                )}

                {tags.map((tag) => (
                  <div
                    key={tag.name}
                    className="px-3 py-4 grid grid-cols-10 border-t border-gray-200 items-center"
                  >
                    <div className="col-span-7 font-semibold">{tag.name}</div>
                    <div className="col-span-2 mx-auto">
                      <input
                        type="checkbox"
                        aria-label={`${t("Default")}: ${tag.name}`}
                        checked={tag.default_checked}
                        onChange={() => handleToggle(tag.name)}
                      />
                    </div>
                    <div className="col-span-1 mx-auto">
                      <Button
                        colour="white"
                        iconBefore="delete"
                        size="small"
                        tooltip={`${t("Delete")} ${tag.name}`}
                        onClick={() => handleDelete(tag.name)}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex space-x-2 items-end">
                <Input
                  label={t("New tag")}
                  name="newTag"
                  placeholder={t("Tag name in Brightcove")}
                  required={false}
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                />
                <Button
                  colour="white"
                  text={t("Add tag")}
                  disabled={!newTag.trim()}
                  onClick={handleAddTag}
                />
              </div>

              <div className="mt-6">
                <Button
                  colour="yellow"
                  disabled={saving || orgUsers.length === 0}
                  text={
                    saving
                      ? t("Saving...")
                      : `${t("Save to all")} ${orgUsers.length} ${t("users")}`
                  }
                  onClick={handleSave}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

export default Tags;
